import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
type Product = {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
  weight: number;
};
// Known catalog for this page (matches the products actually sold).
// Acts as the robust fallback if DOM scanning finds nothing usable.
const FALLBACK_CATALOG: Product[] = [{
  title: 'Kit Educativo 15 en 1',
  editionLabel: 'Combo Completo',
  priceNumber: 21999,
  priceText: '$21.999',
  thumbnailUrl: 'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/BUNDLE.png?v=1780592912&width=120&quality=80&format=webp',
  weight: 0.7
}, {
  title: 'Recorta, Arma y Pega',
  editionLabel: 'Cuadernillo Extra',
  priceNumber: 9999,
  priceText: '$9.999',
  thumbnailUrl: 'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/Recorta_arma_y_pega.png?v=1780592902&width=120&quality=80&format=webp',
  weight: 0.3
}];
const NAMES = ['Sofía', 'Martina', 'Valentina', 'Camila', 'María', 'Lucía', 'Florencia', 'Agustina', 'Julieta', 'Carla', 'Romina', 'Gabriela', 'Mateo', 'Santiago', 'Benjamín', 'Joaquín', 'Lautaro', 'Tomás', 'Facundo', 'Nicolás', 'Diego', 'Gonzalo', 'Federico', 'Matías'];
const PROVINCES = ['Buenos Aires', 'Córdoba', 'Santa Fe', 'Mendoza', 'Tucumán', 'Salta', 'Entre Ríos', 'Misiones', 'Corrientes', 'Chaco', 'Neuquén', 'Río Negro', 'San Juan', 'Jujuy', 'La Pampa', 'Santiago del Estero'];
const pick = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];
const pickWeighted = (catalog: Product[]): Product => {
  const total = catalog.reduce((s, p) => s + (p.weight || 1), 0);
  let r = Math.random() * total;
  for (const p of catalog) {
    r -= p.weight || 1;
    if (r <= 0) return p;
  }
  return catalog[0];
};
// Lightweight DOM price scan — enriches/validates the fallback catalog.
const scanCatalog = (): Product[] => {
  try {
    const priceRegex = /\$\s?\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{2})?/g;
    const bodyText = document.body?.innerText || '';
    const found = bodyText.match(priceRegex);
    // If we can confirm the page actually shows our known prices, trust catalog.
    if (found && found.length > 0) {
      return FALLBACK_CATALOG;
    }
  } catch {
    // ignore — fall through to fallback
  }
  return FALLBACK_CATALOG;
};
export const PurchaseToast = () => {
  const [current, setCurrent] = useState<{
    name: string;
    province: string;
    product: Product;
  } | null>(null);
  const catalogRef = useRef<Product[]>(FALLBACK_CATALOG);
  const recentNames = useRef<string[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    catalogRef.current = scanCatalog();
    const selectName = () => {
      const available = NAMES.filter(n => !recentNames.current.includes(n));
      const pool = available.length > 0 ? available : NAMES;
      const name = pick(pool);
      recentNames.current.push(name);
      if (recentNames.current.length > 10) recentNames.current.shift();
      return name;
    };
    const showOne = () => {
      setCurrent({
        name: selectName(),
        province: pick(PROVINCES),
        product: pickWeighted(catalogRef.current)
      });
      // Visible for 4s, then hide
      hideRef.current = setTimeout(() => setCurrent(null), 4000);
      // Schedule next at random 8–15s after this one appears
      const nextDelay = 8000 + Math.random() * 7000;
      timeoutRef.current = setTimeout(showOne, nextDelay);
    };
    // First toast after 3s
    timeoutRef.current = setTimeout(showOne, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (hideRef.current) clearTimeout(hideRef.current);
    };
  }, []);
  return <div className="fixed bottom-4 inset-x-0 z-[90] flex justify-center px-4 pointer-events-none md:bottom-6" data-id="element-298">
      <AnimatePresence data-id="element-299">
        {current && <motion.div key={`${current.name}-${current.province}-${current.product.title}`} initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-2xl shadow-soft border border-slate-100 p-3 pr-5 flex items-center gap-3 max-w-sm w-full" role="status" aria-live="polite" data-id="element-300">
            <div className="relative flex-shrink-0" data-id="element-301">
              <img src={current.product.thumbnailUrl} alt={current.product.title} width="56" height="56" loading="lazy" decoding="async" className="w-14 h-14 object-contain rounded-xl bg-brand-gray p-1" data-id="element-302" />
              <span className="absolute -bottom-1 -right-1 bg-brand-green rounded-full p-0.5" data-id="element-303">
                <CheckCircle2 className="w-4 h-4 text-white" data-id="element-304" />
              </span>
            </div>
            <div className="min-w-0" data-id="element-305">
              <p className="text-sm text-slate-700 leading-snug" data-id="element-306">
                <strong className="text-brand-navy" data-id="element-307">{current.name}</strong> en{' '}
                <strong className="text-brand-navy" data-id="element-308">{current.province}</strong>{' '}
                acaba de comprar{' '}
                <strong className="text-brand-pink" data-id="element-309">
                  {current.product.title}
                </strong>{' '}
                <span className="text-slate-400" data-id="element-310">
                  ({current.product.editionLabel})
                </span>{' '}
                — <span className="font-bold" data-id="element-311">{current.product.priceText}</span>
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-312">
                Hace unos instantes · Compra verificada
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};